/**
 * Shared types for Holidays feature
 *
 * Holidays are company-level definitions for special days (recurring or one-time)
 * with optional location targeting and operating hours overrides.
 */
import { z } from "zod";
// ===== HOLIDAY HOURS SCHEMA =====
/**
 * Holiday hours period - represents a single open/close window
 */
export const HolidayHoursPeriodSchema = z.object({
    startTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Must be HH:mm format"),
    endTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Must be HH:mm format"),
});
/**
 * Holiday hours - array of open periods (max 5)
 */
export const HolidayHoursSchema = z.array(HolidayHoursPeriodSchema).max(5).nullable().optional();
// ===== INPUT SCHEMAS =====
/**
 * Base fields shared by both recurring and one-time holidays
 */
const BaseHolidayInputSchema = z.object({
    name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
    locationIds: z.array(z.uuid()).default([]),
    companyOpen: z.boolean().default(false),
    allowHoursAdjustment: z.boolean().default(false),
    holidayHours: HolidayHoursSchema,
    payRuleIds: z.array(z.uuid()).default([]),
});
/**
 * Create input for recurring holiday (annual, e.g., Dec 25)
 */
export const CreateRecurringHolidayInputSchema = BaseHolidayInputSchema.extend({
    isRecurring: z.literal(true),
    month: z.number().int().min(1, "Month must be 1-12").max(12, "Month must be 1-12"),
    day: z.number().int().min(1, "Day must be 1-31").max(31, "Day must be 1-31"),
});
/**
 * Create input for one-time holiday (specific date)
 */
export const CreateOneTimeHolidayInputSchema = BaseHolidayInputSchema.extend({
    isRecurring: z.literal(false),
    specificDate: z.coerce.date(),
});
/**
 * Create holiday input - discriminated union by isRecurring
 */
export const CreateHolidayInputSchema = z.discriminatedUnion("isRecurring", [
    CreateRecurringHolidayInputSchema,
    CreateOneTimeHolidayInputSchema,
]);
/**
 * Update holiday input - partial, supports changing recurrence type
 */
export const UpdateHolidayInputSchema = z.object({
    name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or less").optional(),
    isRecurring: z.boolean().optional(),
    month: z.number().int().min(1, "Month must be 1-12").max(12, "Month must be 1-12").optional(),
    day: z.number().int().min(1, "Day must be 1-31").max(31, "Day must be 1-31").optional(),
    specificDate: z.coerce.date().optional(),
    locationIds: z.array(z.uuid()).optional(),
    companyOpen: z.boolean().optional(),
    allowHoursAdjustment: z.boolean().optional(),
    holidayHours: HolidayHoursSchema,
    payRuleIds: z.array(z.uuid()).optional(),
});
// ===== RESPONSE SCHEMAS =====
/**
 * Holiday response schema - uses z.uuid() and z.iso.datetime() for response fields
 */
export const HolidayResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    isRecurring: z.boolean(),
    month: z.number().nullable(),
    day: z.number().nullable(),
    specificDate: z.iso.datetime().nullable(),
    locationIds: z.array(z.uuid()),
    companyOpen: z.boolean(),
    allowHoursAdjustment: z.boolean(),
    holidayHours: HolidayHoursSchema,
    payRuleIds: z.array(z.uuid()),
    companyId: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    nextOccurrence: z.iso.datetime().nullable(), // Computed: next date this holiday occurs
});
/**
 * List holidays response
 */
export const ListHolidaysResponseSchema = z.object({
    data: z.array(HolidayResponseSchema),
    total: z.number().int().nonnegative(),
});
